/**
 * X-MOBILITY-001 — study mobility, as a bounded scoring signal.
 *
 * The question is "for the right course, could you study away from home?".
 * It is a statement of what is POSSIBLE, not a preference, so it must never
 * be an eligibility filter: someone who can only study where they live now
 * still gets a full result set. It buys exactly one thing — a small,
 * bounded nudge towards (or away from) work whose travel load only makes
 * sense if you can move for it.
 *
 * Reach is ordinal: home < state < india < world. "Not sure yet" is treated
 * as the conservative middle (state), never as a scored midpoint of its own.
 */
import type { StudyMobility } from "./types";

export const MOBILITY_LABELS: Record<StudyMobility, string> = {
  world: "Anywhere in the world",
  india: "Anywhere in India",
  state: "Anywhere in my state",
  home: "Only where I live now",
  unsure: "Not sure yet",
};

/** 0 = stay home, 3 = anywhere on earth. */
export const MOBILITY_REACH: Record<StudyMobility, number> = {
  home: 0,
  state: 1,
  unsure: 1, // conservative — never scored as its own midpoint
  india: 2,
  world: 3,
};

export function mobilityReach(m: StudyMobility | undefined): number | undefined {
  return m ? MOBILITY_REACH[m] : undefined;
}

/** One line for the result screen, in the learner's own terms. */
export const MOBILITY_RESULT_NOTE: Record<StudyMobility, string> = {
  world: "You said you could study anywhere in the world, so international routes and travel-heavy work stay on the table.",
  india: "You said you could study anywhere in India, so national institutes and roles that move around stay on the table.",
  state: "You said you could study anywhere in your state, so we've leaned towards routes you can reach from there.",
  home: "You said you'd need to study where you live now, so we've leaned towards routes and roles that work from one place.",
  unsure: "You're not sure yet how far you could go, so we've kept the reach modest. Widen it later and the list widens with it.",
};

/** Short chip text for the review / dossier surfaces. */
export const MOBILITY_CHIP: Record<StudyMobility, string> = {
  world: "Can study anywhere in the world",
  india: "Can study anywhere in India",
  state: "Can study within my state",
  home: "Studying from home only",
  unsure: "Study reach undecided",
};
